import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, Flame } from "lucide-react";
import { useLeaderboard } from "@/api/hooks";
import { useAuth } from "@/lib/AuthContext";
import AppHeader from "@/components/layout/AppHeader";
import GiveawayFooter from "../components/giveaway/GiveawayFooter";

const GOLD_GRADIENT = "linear-gradient(3deg, #806731 5%, #917844 12%, #CCB686 35%, #F0DCAF 51%, #FFEBBF 60%, #FFEBBF 97%)";

function StatCard({ label, value, sub }) {
  return (
    <div
      className="rounded-2xl flex flex-col items-center justify-center gap-3 text-center flex-shrink-0"
      style={{
        background: "#111",
        border: "1px solid #3b3b3b",
        width: 152,
        height: 158,
      }}
    >
      <span className="text-white text-lg font-semibold">{label}</span>
      <span className="text-5xl font-semibold" style={{ color: "#c8f135", lineHeight: "36px" }}>
        {value}
      </span>
      <span className="text-white text-lg font-semibold whitespace-nowrap">{sub}</span>
    </div>
  );
}

function PrizeCard({ campaign }) {
  const prizeDesc = campaign?.prize_description || "זוג כרטיסים למשחק הפתיחה של המונדיאל";
  const pageConfig = campaign?.page_config || {};
  const matchLabel = pageConfig.match_label || 'ארה"ב  -  פרגוואי';
  const homeFlag = pageConfig.home_flag || null;
  const awayFlag = pageConfig.away_flag || null;

  return (
    <div
      className="rounded-2xl flex flex-col items-center justify-center gap-1 flex-shrink-0"
      style={{ background: "#111", width: 322, height: 158 }}
    >
      <span className="text-2xl">🏆</span>
      <div className="text-center px-4" style={{ lineHeight: "26px" }}>
        <p
          className="text-3xl font-semibold bg-clip-text"
          style={{
            backgroundImage: GOLD_GRADIENT,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {prizeDesc.split("\n")[0] || prizeDesc}
        </p>
        {prizeDesc.split("\n")[1] && (
          <p
            className="text-lg font-semibold bg-clip-text"
            style={{
              backgroundImage: GOLD_GRADIENT,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {prizeDesc.split("\n")[1]}
          </p>
        )}
      </div>
      <div className="flex items-center gap-4 mt-1">
        {homeFlag && <img src={homeFlag} alt="" className="w-6 h-6 rounded-full object-cover" />}
        <span className="text-2xl font-semibold text-white whitespace-pre" dir="auto">
          {matchLabel}
        </span>
        {awayFlag && <img src={awayFlag} alt="" className="w-6 h-6 rounded-full object-cover" />}
      </div>
    </div>
  );
}

function UserProfileCard({ user }) {
  const name = user?.full_name || user?.display_name || "";
  const initials = name
    ? name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <div className="flex flex-col items-center gap-3 flex-shrink-0" style={{ width: 240 }}>
      <div
        className="rounded-full flex items-center justify-center text-2xl font-black"
        style={{ width: 80, height: 80, background: "#c8f135", color: "#000" }}
      >
        {initials}
      </div>
      <span className="text-white text-lg font-semibold whitespace-nowrap">{name}</span>
    </div>
  );
}

function PodiumColumn({ entry, place, isCenter }) {
  const medalSrc = { 1: "/medals/gold.png", 2: "/medals/silver.png", 3: "/medals/bronze.png" }[place];

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Medal */}
      <div style={{ height: isCenter ? 104 : 104, marginTop: isCenter ? 0 : 20 }}>
        <img src={medalSrc} alt={`${place}`} className="object-contain" style={{ width: 134, height: 104 }} />
      </div>

      {/* Gradient pedestal */}
      <div
        className="w-full flex flex-col items-center pt-4 pb-3 gap-2"
        style={{
          background: "linear-gradient(180deg, rgba(73,70,16,0.3) 0%, rgba(25,25,25,0) 100%)",
          borderTopLeftRadius: 40,
          borderTopRightRadius: 40,
          minHeight: isCenter ? 128 : 109,
        }}
      >
        {entry ? (
          <>
            <div className="flex items-center gap-3">
              <span className="text-white text-sm font-medium whitespace-nowrap">{entry.display_name}</span>
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0"
                style={{ background: "#c8f135", color: "#000" }}
              >
                {entry.display_name?.[0]?.toUpperCase() || "?"}
              </div>
            </div>
            <div
              className="px-4 py-1 rounded-full flex items-center justify-center"
              style={{ background: "#000" }}
            >
              <span className="text-white text-sm font-medium">{entry.total_points}</span>
            </div>
          </>
        ) : (
          <span className="text-gray-500 text-sm">—</span>
        )}
      </div>
    </div>
  );
}

function LeaderboardRowNew({ entry, index, isCurrentUser }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      className="flex items-center h-14 rounded-2xl px-5 relative"
      style={{
        background: isCurrentUser ? "rgba(200,241,53,0.08)" : "#050709",
        border: isCurrentUser ? "1px solid rgba(200,241,53,0.25)" : "none",
      }}
    >
      {/* Rank (right side in RTL) */}
      <div className="flex items-center gap-3 flex-shrink-0" style={{ width: 77, justifyContent: "flex-end" }}>
        <span className={`text-base font-bold ${isCurrentUser ? "" : "text-white"}`} style={isCurrentUser ? { color: "#c8f135" } : {}}>
          {entry.rank}
        </span>
        <Trophy className="w-4 h-5 flex-shrink-0" style={{ color: isCurrentUser ? "#c8f135" : "#555" }} />
      </div>

      {/* Name (center) */}
      <div className="flex-1 flex items-center gap-4 justify-end px-4">
        <span
          className="text-sm font-medium truncate"
          style={{ color: isCurrentUser ? "#c8f135" : "#fff" }}
        >
          {entry.display_name}
          {isCurrentUser && <span className="mr-2 text-xs opacity-60">(אתה)</span>}
        </span>
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
          style={{ background: isCurrentUser ? "#c8f135" : "#333", color: isCurrentUser ? "#000" : "#fff" }}
        >
          {entry.display_name?.[0]?.toUpperCase() || "?"}
        </div>
      </div>

      {/* Points (left side in RTL) */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <div
          className="flex items-center gap-2 px-4 py-1 rounded-full"
          style={{ background: "#1a1a1a" }}
        >
          <span className="text-white text-sm font-medium">{entry.total_points}</span>
          <Flame className="w-4 h-4" style={{ color: "#c8f135" }} />
        </div>
      </div>
    </motion.div>
  );
}

export default function Leaderboard() {
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("campaign_id") || null;
  const { isAuthenticated, user: authUser, isLoadingAuth } = useAuth();
  const { data, isLoading } = useLeaderboard(campaignId);


  if (isLoading || isLoadingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0a0a0a" }}>
        <div
          className="w-10 h-10 border-4 border-transparent rounded-full animate-spin"
          style={{ borderTopColor: "#c8f135" }}
        />
      </div>
    );
  }

  // Normalize both API response formats:
  //   1) { rankings: [...], my_rank, total_participants, campaign, open_tasks }
  //   2) { entries: [...], campaign } (when use_campaign_system flag is on)
  const raw = data || {};
  const rankings = raw.rankings || (raw.entries || []).map((e) => ({
    rank: e.rank,
    display_name: e.full_name,
    total_points: e.total_points ?? e.campaign_points,
  }));
  const myRank = raw.my_rank || null;
  const totalParticipants = raw.total_participants ?? rankings.length;
  const campaign = raw.campaign || null;
  const openTasks = raw.open_tasks ?? 0;

  const top3 = rankings.slice(0, 3);
  const rest = rankings.slice(3);
  const maxPoints = campaign?.max_points || 602;
  const myPoints = myRank?.total_points || 0;
  const myRankNum = myRank?.rank || 0;

  return (
    <div className="min-h-screen overflow-x-hidden w-full" style={{ background: "#000" }}>
      <AppHeader />

      {/* Spacer for fixed header */}
      <div className="h-14" />

      {/* ── Stats Hero Section ── */}
      <section className="relative overflow-hidden" style={{ background: "#0d0d0d" }}>
        {/* Decorative glow */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            width: 946,
            height: 108,
            background: "rgba(200,241,53,0.1)",
            borderRadius: 189,
            filter: "blur(40px)",
          }}
        />

        <div className="max-w-5xl mx-auto px-4 py-10 relative z-10" dir="rtl">
          <div className="flex items-center gap-5 flex-wrap justify-center lg:flex-nowrap">
            {/* User profile */}
            {isAuthenticated && authUser && (
              <UserProfileCard user={authUser} />
            )}

            {/* Prize card */}
            <PrizeCard campaign={campaign} />

            {/* Stat cards */}
            <StatCard label="נקודות" value={myPoints} sub={`${maxPoints} נק׳ לסיום`} />
            <StatCard label="משימות" value={openTasks} sub="משימות פתוחות" />
            <StatCard label="דירוג" value={`#${myRankNum}`} sub={`מתוך ${totalParticipants}`} />
          </div>
        </div>
      </section>

      {/* ── Leaderboard Section ── */}
      <section
        className="relative"
        style={{ background: "linear-gradient(180deg, #161616 0%, #252525 100%)" }}
      >
        <div className="max-w-5xl mx-auto px-4 py-16" dir="rtl">
          {/* Title */}
          <h2
            className="text-center text-5xl font-bold mb-12 bg-clip-text"
            style={{
              backgroundImage: GOLD_GRADIENT,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            לוח הישגים
          </h2>

          {/* Top 3 Podium */}
          {top3.length > 0 && (
            <div className="grid grid-cols-3 gap-4 mb-10 max-w-3xl mx-auto items-end">
              {/* 3rd place (right in RTL visual, left column) */}
              <PodiumColumn entry={top3[2]} place={3} isCenter={false} />
              {/* 1st place (center) */}
              <PodiumColumn entry={top3[0]} place={1} isCenter={true} />
              {/* 2nd place (left in RTL visual, right column) */}
              <PodiumColumn entry={top3[1]} place={2} isCenter={false} />
            </div>
          )}

          {/* Column headers */}
          {rest.length > 0 && (
            <>
              <div className="flex items-center justify-between max-w-3xl mx-auto mb-3 px-5">
                <span className="text-sm font-medium" style={{ color: "#898989" }}>מיקום</span>
                <span className="text-sm font-medium" style={{ color: "#898989" }}>שם</span>
                <span className="text-sm font-medium" style={{ color: "#898989" }}>ניקוד</span>
              </div>

              {/* Ranking rows */}
              <div className="flex flex-col gap-3 max-w-3xl mx-auto">
                {rest.map((entry, i) => (
                  <LeaderboardRowNew
                    key={entry.rank}
                    entry={entry}
                    index={i}
                    isCurrentUser={myRank && entry.rank === myRank.rank}
                  />
                ))}
              </div>
            </>
          )}

          {/* Current user row if outside visible range */}
          {myRank && myRankNum > rankings.length && (
            <div className="max-w-3xl mx-auto mt-6">
              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px" style={{ background: "#333" }} />
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.25)" }}>הדירוג שלך</span>
                <div className="flex-1 h-px" style={{ background: "#333" }} />
              </div>
              <LeaderboardRowNew
                entry={{ ...myRank, rank: myRankNum }}
                index={0}
                isCurrentUser={true}
              />
            </div>
          )}

          {rankings.length === 0 && (
            <div className="text-center py-16">
              <p className="text-5xl mb-4">🚀</p>
              <p className="font-bold text-white text-lg">היה הראשון!</p>
              <p className="text-sm mt-2" style={{ color: "rgba(255,255,255,0.4)" }}>
                עדיין אין משתתפים בלידרבורד
              </p>
            </div>
          )}
        </div>
      </section>

      <GiveawayFooter />
    </div>
  );
}
